import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Inbox, Zap, Layers, ExternalLink, RefreshCw, AlertTriangle, Plus, Send } from "lucide-react";

const COLUMNS = [
  { key: "submitted", label: "Enviada", statuses: ["submitted", "ado_pending"] },
  { key: "triaged", label: "Triaje", statuses: ["triaged"] },
  { key: "in_progress", label: "En progreso", statuses: ["in_progress"] },
  { key: "closed", label: "Cerrada", statuses: ["done", "rejected"] },
];
const NEXT_STATUS = { submitted: "triaged", ado_pending: "triaged", triaged: "in_progress", in_progress: "done" };
const PRIO_COLOR = { P1: "bg-red-100 text-red-600", P2: "bg-amber-100 text-amber-600", P3: "bg-blue-100 text-blue-600", P4: "bg-gray-100 text-gray-500" };

function SlaCountdown({ sla_due_at, closed }) {
  if (!sla_due_at || closed) return null;
  const due = new Date(sla_due_at);
  const overdue = due < new Date();
  return (
    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${overdue ? "bg-red-100 text-red-600" : "bg-gray-100 text-gray-500"}`}>
      {overdue ? "SLA vencido" : `SLA ${due.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit" })}`}
    </span>
  );
}

function Card({ item, isAdmin, onAdvance, onOpenAdo }) {
  const closed = item.status === "done" || item.status === "rejected";
  const Icon = item.track === "fast_track" ? Zap : Layers;
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-3 space-y-2 hover:shadow-sm transition-shadow" data-testid={`demanda-card-${item.id}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-semibold text-gray-900 leading-snug">{item.titulo}</p>
        <Icon size={14} className={item.track === "fast_track" ? "text-amber-500 flex-shrink-0" : "text-[#007934] flex-shrink-0"} />
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${PRIO_COLOR[item.prioridad] || PRIO_COLOR.P4}`}>{item.prioridad}</span>
        <span className="text-[10px] text-gray-400">{item.ado_project}</span>
        {item.iniciativa_titulo && <span className="text-[10px] text-purple-500 truncate max-w-[90px]" title={item.iniciativa_titulo}>{item.iniciativa_titulo}</span>}
        <SlaCountdown sla_due_at={item.sla_due_at} closed={closed} />
        {!item.azure_id && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-orange-100 text-orange-600">ADO pendiente</span>}
      </div>
      <div className="flex items-center justify-between pt-1">
        <span className="text-[11px] text-gray-500">{item.solicitante}</span>
        <div className="flex items-center gap-2">
          {item.azure_id && (
            <a href={item.azure_url} target="_blank" rel="noreferrer" onClick={() => onOpenAdo?.(item)}
              className="text-[11px] font-semibold text-[#007934] hover:underline flex items-center gap-0.5">
              #{item.azure_id} <ExternalLink size={10} />
            </a>
          )}
          {!closed && NEXT_STATUS[item.status] && (
            <button onClick={() => onAdvance(item, NEXT_STATUS[item.status])} data-testid={`advance-btn-${item.id}`}
              className="text-[11px] font-semibold text-gray-500 hover:text-[#007934]">Avanzar →</button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Demanda() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("todas");
  const [filters, setFilters] = useState({ ado_project: "", prioridad: "", solicitante: "", sin_azure_id: false });
  const [items, setItems] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 300 };
      if (tab !== "todas") params.track = tab;
      if (filters.ado_project) params.ado_project = filters.ado_project;
      if (filters.prioridad) params.prioridad = filters.prioridad;
      if (filters.solicitante) params.solicitante = filters.solicitante;
      if (filters.sin_azure_id) params.sin_azure_id = true;
      const [{ data: list }, { data: k }] = await Promise.all([
        axios.get(`${API}/demanda/solicitudes`, { params }),
        axios.get(`${API}/demanda/kpis`),
      ]);
      setItems(list.items || []);
      setKpis(k);
    } catch {
      toast.error("Error cargando la cola de Demanda");
    } finally {
      setLoading(false);
    }
  }, [tab, filters]);

  useEffect(() => { load(); }, [load]);

  const advance = async (item, next) => {
    try {
      await axios.patch(`${API}/demanda/solicitudes/${item.id}`, { status: next });
      toast.success("Estado actualizado");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo actualizar");
    }
  };

  return (
    <div className="p-6 space-y-6 w-full" data-testid="demanda-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-['Outfit']">Gestión de la Demanda</h1>
          <p className="text-sm text-gray-500 mt-0.5">Cola de intake Fast track / Desarrollo hacia Azure DevOps</p>
        </div>
        <Link to="/solicitud-demanda" data-testid="nueva-solicitud-link"
          className="flex items-center gap-2 bg-[#007934] text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-[#006528] shadow-sm">
          <Plus size={16} /> Nueva solicitud
        </Link>
      </div>

      {kpis && (
        <div className="grid grid-cols-4 gap-3">
          {[
            { key: "fast-open", label: "Fast track abiertas", value: kpis.fast_open, icon: Zap, color: "text-amber-600" },
            { key: "dev-open", label: "Desarrollo abiertas", value: kpis.dev_open, icon: Layers, color: "text-[#007934]" },
            { key: "sla-breached", label: "SLA vencidas", value: kpis.sla_breached, icon: AlertTriangle, color: "text-red-600" },
            { key: "ado-pending", label: "Pendientes en ADO", value: kpis.ado_pending, icon: Send, color: "text-gray-500" },
          ].map(({ key, label, value, icon: Icon, color }) => (
            <div key={label} data-testid={`kpi-${key}`} className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3">
              <Icon size={18} className={color} />
              <div><p className={`text-xl font-bold ${color}`}>{value}</p><p className="text-xs text-gray-400">{label}</p></div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {[{ id: "todas", label: "Todas" }, { id: "fast_track", label: "Fast track" }, { id: "desarrollo", label: "Desarrollo" }].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${tab === t.id ? "bg-white text-[#007934] shadow-sm" : "text-gray-500"}`}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2 flex-wrap">
          <input placeholder="Solicitante..." value={filters.solicitante} data-testid="filter-solicitante-demanda"
            onChange={e => setFilters(f => ({ ...f, solicitante: e.target.value }))}
            className="border border-gray-200 rounded-lg px-3 py-1.5 text-xs w-36 focus:outline-none focus:border-[#007934]" />
          <select value={filters.prioridad} data-testid="filter-prioridad-demanda"
            onChange={e => setFilters(f => ({ ...f, prioridad: e.target.value }))}
            className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs">
            <option value="">Prioridad</option>
            {["P1", "P2", "P3", "P4"].map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <label className="flex items-center gap-1.5 text-xs text-gray-500 bg-white border border-gray-200 rounded-lg px-2.5 py-1.5 cursor-pointer">
            <input type="checkbox" checked={filters.sin_azure_id} data-testid="filter-sin-azure-id"
              onChange={e => setFilters(f => ({ ...f, sin_azure_id: e.target.checked }))} />
            Sin ADO
          </label>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><RefreshCw className="animate-spin text-[#007934]" size={24} /></div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 p-16 text-center">
          <Inbox size={36} className="mx-auto mb-3 text-gray-200" />
          <p className="font-semibold text-gray-400">Sin solicitudes en esta vista</p>
        </div>
      ) : (
        <div className="grid grid-cols-4 gap-4" data-testid="demanda-kanban">
          {COLUMNS.map(col => {
            const colItems = items.filter(i => col.statuses.includes(i.status));
            return (
              <div key={col.key} className="space-y-2" data-testid={`kanban-col-${col.key}`}>
                <div className="flex items-center justify-between px-1">
                  <p className="text-xs font-bold text-gray-500 uppercase">{col.label}</p>
                  <span className="text-xs font-semibold text-gray-400 bg-gray-100 rounded-full px-2">{colItems.length}</span>
                </div>
                <div className="space-y-2 min-h-[100px]">
                  {colItems.map(item => <Card key={item.id} item={item} isAdmin={isAdmin} onAdvance={advance} />)}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
